import { useState } from "react"

import type { UpdatesSpec } from "./proposal-updates"
import type { UpdatesRunner } from "./use-updates-runner"

/** What the section rail shows while the panel is up or minimized. */
export type UpdatesRailState = {
  /** The panel is open over the rail's edge, so the rail steps aside. */
  readonly hidden: boolean
  /** Sections whose figures are out of date, badged while minimized. */
  readonly staleSections: ReadonlySet<string>
}

export type UpdatesPanelState<K extends string> = {
  /** Something is out of date, running, or just fixed. */
  readonly active: boolean
  readonly open: boolean
  /** Show the tab that reopens the panel. */
  readonly minimized: boolean
  /** The rows to show; while the panel slides away, the ones it last showed. */
  readonly rows: readonly K[]
  readonly rail: UpdatesRailState
  readonly onMinimize: () => void
  readonly onRestore: () => void
}

/**
 * When the Updates panel is open, minimized or gone. It slides in while an
 * update is needed and away once everything is current. Minimizing holds for
 * the updates on screen then; a new one reopens it.
 */
export const useUpdatesPanelState = <K extends string>({
  spec,
  needed,
  runner,
  enabled,
}: {
  spec: Pick<UpdatesSpec<K>, `order` | `sections`>
  /** Updates needed, in fix order. */
  needed: readonly K[]
  runner: Pick<UpdatesRunner<K>, `done` | `running`>
  enabled: boolean
}): UpdatesPanelState<K> => {
  const [minimizedFor, setMinimizedFor] = useState<readonly K[] | null>(null)

  const rows = spec.order.filter(
    (key) =>
      needed.includes(key) || runner.done.has(key) || runner.running === key,
  )
  const active = enabled && rows.length > 0
  // The panel stays mounted so it can slide in and out; while it slides away
  // it keeps the rows it last showed, all current.
  const [lastRows, setLastRows] = useState<readonly K[]>(rows)
  if (rows.length > 0 && rows.join() !== lastRows.join()) setLastRows(rows)
  // Once nothing is out of date, the next update opens the panel again.
  if (minimizedFor !== null && needed.length === 0) setMinimizedFor(null)
  const minimized =
    minimizedFor !== null && needed.every((key) => minimizedFor.includes(key))
  const open = active && !minimized

  const staleSections = new Set(needed.flatMap((key) => spec.sections[key]))

  return {
    active,
    open,
    minimized: active && minimized && needed.length > 0,
    rows: active ? rows : lastRows,
    rail: {
      hidden: open,
      staleSections: active && minimized ? staleSections : new Set(),
    },
    onMinimize: () => setMinimizedFor(needed),
    onRestore: () => setMinimizedFor(null),
  }
}
